import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
} from '@mui/material';
import GoogleMapsWrapper from '../UserProfile/components/UserDetails/GoogleMapsWrapper';
import BrowseHandymanList from '../BrowseHandyman/BrowseHandymanList';
import { GlassColors } from '../../components/glassmorphism/GlassmorphismStyles';

type Props = {
  variant?: 'button' | 'link';
  children: React.ReactNode;

  preselectedCategoryKey?: string;
  isNotHandyman: boolean;
  excludeFilters: any[];
  address: string | null;
  radiusKm?: number;

  actionButtonTitle: string;
  onSelect: (handymanId: string) => Promise<void> | void;

  disabled?: boolean;
};

type SelectedHandyman = {
  id: string;
  name: string;
  categories: string[];
  city?: string;
  cityDistrict?: string;
};

export default function SelectHandymanTrigger({
  variant = 'button',
  children,
  preselectedCategoryKey,
  isNotHandyman,
  excludeFilters,
  address,
  radiusKm = 10,
  actionButtonTitle,
  onSelect,
  disabled,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleOpen = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setOpen(true);
  };

  const handleClose = () => {
    if (submitting) return;
    setOpen(false);
  };

  const handlePick = async (handyman?: SelectedHandyman) => {
    if (!handyman?.id) return;

    try {
      setSubmitting(true);
      await onSelect(handyman.id);
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const triggerCommonStyle: React.CSSProperties =
    variant === 'link'
      ? { cursor: disabled ? 'not-allowed' : 'pointer', display: 'flex' }
      : { display: 'inline-block' };

  return (
    <>
      {variant === 'button' ? (
        <span onClick={handleOpen} style={triggerCommonStyle}>
          {children}
        </span>
      ) : (
        <span
          onClick={handleOpen}
          role='link'
          aria-disabled={disabled}
          style={triggerCommonStyle}
        >
          {children}
        </span>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth='md'
        sx={{
          '& .MuiPaper-root': {
            background: isDark
              ? GlassColors.dark.primaryBackground
              : GlassColors.light.primaryBackground,
          },
        }}
      >
        <DialogTitle sx={{ pb: 0 }}>Select a Handyman</DialogTitle>
        <DialogContent sx={{ '& .btn-holder': { py: '16px' } }}>
          <GoogleMapsWrapper>
            <BrowseHandymanList
              preselectedCategoryKey={preselectedCategoryKey}
              isNotHandyman={isNotHandyman}
              excludeFilters={excludeFilters}
              address={address}
              radiusKm={radiusKm}
              actionButtonTitle={
                submitting ? 'Selecting...' : actionButtonTitle
              }
              onAction={handlePick}
              needFilters={false}
            />
          </GoogleMapsWrapper>
        </DialogContent>
      </Dialog>
    </>
  );
}
